// Task Cards — Essentials & Basics Set
export const ESSENTIALS = [
  {
    id: "aperture",
    photo: "/taskcards/aperture.jpg",
    icon: "Aperture",
    title: { en: "Aperture", es: "Apertura" },
    tagline: { en: "The pupil of the lens — controls light and background blur.", es: "La pupila del objetivo — controla la luz y el desenfoque del fondo." },
    settings: [
      { k: "aperture", v: "f/1.8–f/2.8 → blurry BG" },
      { k: "aperture", v: "f/8–f/11 → all sharp" },
      { k: "mode", v: "A / Av (Aperture Priority)" },
    ],
    tips: {
      en: [
        "Low f-number = wide opening = more light + shallow depth of field.",
        "Portraits: f/1.8–f/2.8 for creamy subject separation.",
        "Landscapes & groups: f/8–f/11 to keep everything sharp.",
        "Past f/16 diffraction softens detail — avoid unless you need the depth.",
      ],
      es: [
        "Número f bajo = abertura amplia = más luz + poca profundidad de campo.",
        "Retratos: f/1.8–f/2.8 para separar al sujeto con fondo cremoso.",
        "Paisajes y grupos: f/8–f/11 para mantener todo nítido.",
        "Pasando f/16 la difracción ablanda el detalle — evitalo salvo que necesites profundidad.",
      ],
    },
    remember: { en: "Small number = big blur. Big number = all sharp.", es: "Número pequeño = mucho desenfoque. Número grande = todo nítido." },
  },
  {
    id: "shutter-speed",
    photo: "/taskcards/shutter-speed.jpg",
    icon: "Timer",
    title: { en: "Shutter Speed", es: "Velocidad de Obturación" },
    tagline: { en: "How long the sensor sees light — freezes or blurs motion.", es: "Cuanto tiempo ve la luz el sensor — congela o difumina el movimiento." },
    settings: [
      { k: "shutter", v: "1/500s+ → freeze action" },
      { k: "shutter", v: "1/60–1/200 → people" },
      { k: "shutter", v: "1s–30s → light trails (tripod)" },
    ],
    tips: {
      en: [
        "Freeze sports/kids: 1/500s or faster.",
        "Handheld rule: shutter ≥ 1/focal length (e.g. 1/100s at 100mm).",
        "Below 1/60s handheld risks blur — use a tripod or raise ISO.",
        "Long exposures (waterfalls, stars) need a tripod and low ISO.",
      ],
      es: [
        "Congelar deportes/ninos: 1/500s o más rápido.",
        "Regla a pulso: velocidad ≥ 1/distancia focal (ej. 1/100s a 100mm).",
        "Bajo 1/60s a pulso hay riesgo de trepidación — usa trípode o sube el ISO.",
        "Exposiciones largas (cascadas, estrellas) requieren trípode e ISO bajo.",
      ],
    },
    remember: { en: "Fast = freeze. Slow = blur (and use a tripod).", es: "Rápida = congela. Lenta = difumina (y usa trípode)." },
  },
  {
    id: "iso",
    photo: "/taskcards/iso.jpg",
    icon: "FilmStrip",
    title: { en: "ISO", es: "ISO" },
    tagline: { en: "The sensor's sensitivity to light — your last resort for brightness.", es: "La sensibilidad del sensor a la luz — tu último recurso para el brillo." },
    settings: [
      { k: "iso", v: "100–200 → bright daylight" },
      { k: "iso", v: "400–1600 → indoors/shade" },
      { k: "iso", v: "3200+ → night/dim" },
    ],
    tips: {
      en: [
        "Keep ISO as low as the light allows for the cleanest image.",
        "Raise ISO only after aperture and shutter are set.",
        "High ISO adds grain (noise) — full-frame sensors handle it best.",
        "Modern noise reduction (and RAW) recovers a lot, so don't fear ISO 3200.",
      ],
      es: [
        "Mantén el ISO tan bajo como permita la luz para la imagen más limpia.",
        "Sube el ISO solo después de fijar apertura y velocidad.",
        "El ISO alto añade grano (ruido) — los sensores full frame lo manejan mejor.",
        "La reducción de ruido moderna (y el RAW) recupera mucho, no temas al ISO 3200.",
      ],
    },
    remember: { en: "Low ISO = clean. High ISO = brighter but grainier.", es: "ISO bajo = limpio. ISO alto = más brillante pero con grano." },
  },
  {
    id: "white-balance",
    icon: "Thermometer",
    title: { en: "White Balance", es: "Balance de Blancos" },
    tagline: { en: "Tells the camera what 'white' is so colors look true.", es: "Le dice a la cámara que es 'blanco' para que los colores se vean reales." },
    settings: [
      { k: "wb", v: "Daylight ≈ 5500K" },
      { k: "wb", v: "Cloudy/Shade → warmer" },
      { k: "wb", v: "Tungsten ≈ 3200K" },
    ],
    tips: {
      en: [
        "Auto WB is fine most of the time; set it manually for mixed light.",
        "Cloudy/Shade presets add warmth on grey days.",
        "Shoot RAW to change white balance freely after the shot.",
        "A grey card gives a perfect custom white balance reference.",
      ],
      es: [
        "El BB automático sirve casi siempre; ajustalo manual con luz mixta.",
        "Los modos Nublado/Sombra anaden calidez en días grises.",
        "Dispara en RAW para cambiar el balance de blancos libremente después.",
        "Una tarjeta gris da una referencia de balance personalizada perfecta.",
      ],
    },
    remember: { en: "Warm light = lower K. Cool light = higher K.", es: "Luz cálida = K bajo. Luz fría = K alto." },
  },
  {
    id: "exposure-triangle",
    icon: "Triangle",
    title: { en: "The Exposure Triangle", es: "El Triángulo de Exposición" },
    tagline: { en: "Aperture, Shutter and ISO together decide your exposure.", es: "Apertura, Velocidad e ISO juntos deciden tu exposición." },
    settings: [
      { k: "aperture", v: "controls light + depth" },
      { k: "shutter", v: "controls light + motion" },
      { k: "iso", v: "controls light + noise" },
    ],
    tips: {
      en: [
        "Change one, you must compensate with another to keep brightness.",
        "Each 'stop' doubles or halves the light.",
        "Decide your priority first (blur? motion? noise?), then balance the other two.",
        "Manual mode lets you set all three independently.",
      ],
      es: [
        "Si cambias uno, compensa con otro para mantener el brillo.",
        "Cada 'paso' duplica o reduce a la mitad la luz.",
        "Decide tu prioridad primero (desenfoque, movimiento, ruido) y equilibra los otros dos.",
        "El modo Manual te deja ajustar los tres de forma independiente.",
      ],
    },
    remember: { en: "Three controls, one exposure. Move one, adjust another.", es: "Tres controles, una exposición. Mueve uno, ajusta otro." },
  },
  {
    id: "depth-of-field",
    photo: "/taskcards/depth-of-field.jpg",
    icon: "Stack",
    title: { en: "Depth of Field", es: "Profundidad de Campo" },
    tagline: { en: "How much of the scene is in sharp focus, front to back.", es: "Cuanto de la escena esta enfocado nitidamente, de adelante hacia atrás." },
    settings: [
      { k: "aperture", v: "f/1.8–f/2.8 → shallow" },
      { k: "aperture", v: "f/8–f/16 → deep" },
      { k: "focal", v: "longer lens → shallower" },
    ],
    tips: {
      en: [
        "Three things make it shallower: wider aperture, longer lens, closer subject.",
        "Shallow DOF isolates a subject; deep DOF keeps a whole scene sharp.",
        "Get closer to your subject to increase background blur at any aperture.",
        "For groups, stop down so the back row stays sharp.",
      ],
      es: [
        "Tres cosas la reducen: apertura más amplia, lente más largo, sujeto más cerca.",
        "Poca PdC aisla al sujeto; mucha PdC mantiene nítida toda la escena.",
        "Acercate al sujeto para aumentar el desenfoque del fondo en cualquier apertura.",
        "Para grupos, cierra el diafragma para que la fila de atrás quede nítida.",
      ],
    },
    remember: { en: "Wide + close + long = blurry background.", es: "Abierto + cerca + largo = fondo desenfocado." },
  },
  {
    id: "composition-basics",
    photo: "/taskcards/composition-basics.jpg",
    icon: "GridFour",
    title: { en: "Composition Basics", es: "Fundamentos de Composición" },
    tagline: { en: "Arrange the frame so the eye goes where you want it.", es: "Organiza el encuadre para que el ojo vaya donde quieres." },
    tips: {
      en: [
        "Rule of thirds: place key elements on the gridlines, not dead center.",
        "Use leading lines (roads, fences) to pull the eye into the scene.",
        "Simplify — remove distractions from the edges of the frame.",
        "Mind the background as much as the subject.",
        "Leave space in the direction a subject is looking or moving.",
      ],
      es: [
        "Regla de los tercios: coloca lo importante en las líneas, no en el centro muerto.",
        "Usa líneas guía (caminos, vallas) para llevar el ojo a la escena.",
        "Simplifica — quita distracciones de los bordes del encuadre.",
        "Cuida el fondo tanto como al sujeto.",
        "Deja espacio en la dirección a la que mira o se mueve el sujeto.",
      ],
    },
    remember: { en: "Simplify, place off-center, guide the eye.", es: "Simplifica, descentra, guía la mirada." },
  },
  {
    id: "histogram",
    icon: "ChartBar",
    title: { en: "Histogram", es: "Histograma" },
    tagline: { en: "A graph of brightness — the truth your LCD can't show.", es: "Un gráfico del brillo — la verdad que tu LCD no muestra." },
    tips: {
      en: [
        "Left = shadows, right = highlights, middle = midtones.",
        "Spikes climbing the right wall = blown (lost) highlights.",
        "Spikes on the left wall = crushed (lost) shadow detail.",
        "Aim to keep data off both edges; 'expose to the right' for cleaner files.",
        "Trust the histogram over the LCD, which lies in bright sun.",
      ],
      es: [
        "Izquierda = sombras, derecha = altas luces, centro = tonos medios.",
        "Picos pegados a la pared derecha = altas luces quemadas (perdidas).",
        "Picos pegados a la pared izquierda = sombras sin detalle (perdidas).",
        "Busca mantener datos lejos de ambos bordes; 'expón a la derecha' para archivos más limpios.",
        "Confia en el histograma más que en la LCD, que engana con sol fuerte.",
      ],
    },
    remember: { en: "Don't let the graph touch the walls.", es: "No dejes que el gráfico toque las paredes." },
  },
  {
    id: "manual-mode",
    icon: "Aperture",
    title: { en: "Manual Mode (M)", es: "Modo Manual (M)" },
    tagline: { en: "You control aperture, shutter and ISO — full creative power.", es: "Tu controlas apertura, velocidad e ISO — poder creativo total." },
    settings: [
      { k: "mode", v: "M" },
      { k: "metering", v: "watch the meter at 0" },
      { k: "iso", v: "set last, after f & shutter" },
    ],
    tips: {
      en: [
        "Set aperture for the look, shutter for motion, ISO to balance.",
        "Use the in-viewfinder meter: aim for 0 (or bias for effect).",
        "Manual is best when light is constant (studio, indoors).",
        "Take a test shot, check the histogram, then fine-tune.",
      ],
      es: [
        "Pon la apertura para el aspecto, la velocidad para el movimiento, el ISO para equilibrar.",
        "Usa el medidor del visor: busca el 0 (o desviate para un efecto).",
        "Manual rinde mejor cuando la luz es constante (estudio, interiores).",
        "Haz una toma de prueba, revisa el histograma y ajusta.",
      ],
    },
    remember: { en: "Aperture for look, shutter for motion, ISO to balance.", es: "Apertura para el aspecto, velocidad para el movimiento, ISO para equilibrar." },
  },
  {
    id: "raw-vs-jpeg",
    icon: "FileImage",
    title: { en: "RAW vs JPEG", es: "RAW vs JPEG" },
    tagline: { en: "Edit-everything negative vs ready-to-share photo.", es: "Negativo editable vs foto lista para compartir." },
    tips: {
      en: [
        "RAW keeps all sensor data — recover highlights, shadows, white balance.",
        "JPEG is smaller, sharpened and finished in-camera — great when speed matters.",
        "Shoot RAW (or RAW+JPEG) whenever the photo matters or light is tricky.",
        "RAW files are big and need editing software to finish.",
        "White balance and exposure are far more forgiving in RAW.",
      ],
      es: [
        "RAW guarda todos los datos del sensor — recupera altas luces, sombras, balance.",
        "JPEG es más pequeño, enfocado y terminado en cámara — ideal cuando importa la rapidez.",
        "Dispara RAW (o RAW+JPEG) cuando la foto importa o la luz es complicada.",
        "Los archivos RAW son grandes y necesitan software para terminarse.",
        "El balance de blancos y la exposición perdonan mucho más en RAW.",
      ],
    },
    remember: { en: "RAW = flexibility. JPEG = convenience.", es: "RAW = flexibilidad. JPEG = comodidad." },
  },
  {
    id: "metering-modes",
    icon: "Crosshair",
    title: { en: "Metering Modes", es: "Modos de Medición" },
    tagline: { en: "How the camera measures light to suggest an exposure.", es: "Como la cámara mide la luz para sugerir una exposición." },
    settings: [
      { k: "metering", v: "Evaluative/Matrix → default" },
      { k: "metering", v: "Center-weighted → portraits" },
      { k: "metering", v: "Spot → tricky/backlight" },
    ],
    tips: {
      en: [
        "Evaluative/Matrix reads the whole scene — best all-rounder.",
        "Spot meters a tiny area — perfect for backlit or high-contrast subjects.",
        "Center-weighted favors the middle — handy for portraits.",
        "If exposure looks off, switch modes or use exposure compensation.",
      ],
      es: [
        "Evaluativa/Matricial lee toda la escena — la más equilibrada.",
        "Puntual mide un area diminuta — ideal para sujetos a contraluz o alto contraste.",
        "Ponderada al centro favorece el medio — útil en retratos.",
        "Si la exposición falla, cambia de modo o usa compensación de exposición.",
      ],
    },
    remember: { en: "Matrix for most. Spot for backlight.", es: "Matricial para casi todo. Puntual para contraluz." },
  },
  {
    id: "focus-modes",
    icon: "Target",
    title: { en: "Focus Modes (AF-S / AF-C)", es: "Modos de Enfoque (AF-S / AF-C)" },
    tagline: { en: "Lock focus on still subjects or track moving ones.", es: "Bloquea el enfoque en sujetos quietos o sigue a los que se mueven." },
    settings: [
      { k: "focus", v: "AF-S / One-Shot → still" },
      { k: "focus", v: "AF-C / AI Servo → moving" },
      { k: "focus", v: "Single point → precision" },
    ],
    tips: {
      en: [
        "AF-S (One-Shot): locks once — for portraits, landscapes, still subjects.",
        "AF-C (AI Servo): keeps tracking — for sports, kids, wildlife.",
        "Use a single AF point for precise placement (e.g. the eye).",
        "Eye-AF (modern cameras) nails portrait focus automatically.",
      ],
      es: [
        "AF-S (One-Shot): bloquea una vez — para retratos, paisajes, sujetos quietos.",
        "AF-C (AI Servo): sigue continuamente — para deportes, niños, fauna.",
        "Usa un solo punto AF para colocar el enfoque con precisión (ej. el ojo).",
        "El Eye-AF (cámaras modernas) clava el enfoque del retrato automáticamente.",
      ],
    },
    remember: { en: "Still = AF-S. Moving = AF-C.", es: "Quieto = AF-S. En movimiento = AF-C." },
  },
  {
    id: "focal-length-crop",
    icon: "Ruler",
    title: { en: "Focal Length & Crop Factor", es: "Distancia Focal y Factor de Recorte" },
    tagline: { en: "How zoomed-in your view is — and how sensor size changes it.", es: "Que tan acercada es tu vista — y como el tamaño del sensor la cambia." },
    settings: [
      { k: "focal", v: "16–35mm → wide" },
      { k: "focal", v: "50mm → natural" },
      { k: "focal", v: "70–300mm → telephoto" },
    ],
    tips: {
      en: [
        "Wide (16–35mm): landscapes, interiors, big scenes.",
        "Standard (35–85mm): everyday, portraits, street.",
        "Telephoto (100mm+): sports, wildlife, compression.",
        "APS-C crop (×1.5/1.6) makes lenses act longer: a 50mm ≈ 75–80mm.",
      ],
      es: [
        "Angular (16–35mm): paisajes, interiores, escenas amplias.",
        "Estándar (35–85mm): diario, retratos, calle.",
        "Telefoto (100mm+): deportes, fauna, compresión.",
        "El recorte APS-C (×1.5/1.6) hace que los lentes actuen más largos: un 50mm ≈ 75–80mm.",
      ],
    },
    remember: { en: "Short = wide view. Long = zoomed in.", es: "Corto = vista amplia. Largo = acercado." },
  },
  {
    id: "flash-basics",
    photo: "/taskcards/flash-basics.jpg",
    icon: "Lightning",
    title: { en: "Flash Basics", es: "Fundamentos del Flash" },
    tagline: { en: "Add light, freeze motion, fill shadows — without the harshness.", es: "Añade luz, congela el movimiento, rellena sombras — sin dureza." },
    settings: [
      { k: "shutter", v: "≤ sync speed (≈1/200s)" },
      { k: "mode", v: "TTL → auto / Manual → control" },
      { k: "tip", v: "bounce off ceiling/wall" },
    ],
    tips: {
      en: [
        "Never point bare flash straight at faces — bounce it off a ceiling or wall.",
        "Fill flash outdoors lifts shadows under hats and harsh midday sun.",
        "Stay at or below your sync speed (often 1/200s) to avoid black bands.",
        "Drag the shutter (slower speed) to let ambient background light in.",
      ],
      es: [
        "Nunca apuntes el flash directo a las caras — rebotalo en el techo o la pared.",
        "El flash de relleno en exteriores aclara sombras bajo sombreros y sol fuerte.",
        "Mantente al/o bajo la velocidad de sincronización (a menudo 1/200s) para evitar bandas negras.",
        "Arrastra el obturador (velocidad lenta) para dejar entrar la luz ambiente del fondo.",
      ],
    },
    remember: { en: "Bounce it, don't blast it.", es: "Rebotalo, no lo dispares de frente." },
  },
  {
    id: "sunny-16-film",
    icon: "Sun",
    title: { en: "Sunny 16 (Film Exposure)", es: "Sunny 16 (Exposición en Film)" },
    tagline: { en: "Expose film correctly with no light meter — just read the sky.", es: "Expón film correctamente sin fotómetro — solo lee el cielo." },
    settings: [
      { k: "shutter", v: "shutter ≈ 1/ISO (ISO 400 → 1/500s)" },
      { k: "aperture", v: "f/16 sun · f/8 cloud · f/4 shade" },
      { k: "iso", v: "one ISO for the whole roll" },
    ],
    tips: {
      en: [
        "Set shutter to 1/ISO, then change ONLY the aperture as the light changes.",
        "Bright sun, hard shadows = f/16. Hazy = f/11. Overcast = f/8.",
        "Heavy overcast = f/5.6. Open shade or sunset = f/4.",
        "Open up 1 stop for backlit subjects or very dark scenes.",
      ],
      es: [
        "Pon la velocidad en 1/ISO, luego cambia SOLO la apertura según cambia la luz.",
        "Sol brillante, sombras duras = f/16. Bruma = f/11. Nublado = f/8.",
        "Muy nublado = f/5.6. Sombra abierta o atardecer = f/4.",
        "Abre 1 paso para sujetos a contraluz o escenas muy oscuras.",
      ],
    },
    remember: { en: "Shutter = 1/ISO. Then let the aperture follow the light.", es: "Velocidad = 1/ISO. Luego deja que la apertura siga la luz." },
  },
];
